import { useRef, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';
import type { FieldValues, Path, RegisterOptions, UseFormRegister } from 'react-hook-form';

interface TextAreaProps<T extends FieldValues> extends InputHTMLAttributes<HTMLTextAreaElement> {
  register: UseFormRegister<T>;
  registerName: Path<T>;
  setValue?: (value: string) => void;
  label?: string;
  registerProps?: RegisterOptions<T, Path<T>>;
  isSecondary?: boolean;
  isDisabled?: boolean;
  error?: FieldValues['errors'];
  placeholder?: string;
  whisper?: string[];
  whisperLabel?: string;
  inputClassName?: string;
  absoluteLabel?: boolean;
}

export const TextArea = <T extends FieldValues>({
  label,
  register,
  registerName,
  registerProps,
  isSecondary,
  isDisabled,
  error,
  placeholder,
  className,
  inputClassName,
  whisper,
  whisperLabel,
  setValue,
  absoluteLabel,
  ...rest
}: TextAreaProps<T>) => {
  const { ref, ...others } = register(registerName, registerProps);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const variantClass = isSecondary ? 'bg-primary border-1 border-secondary-light' : 'bg-transparent border-1 border-primary';

  const handleWhisperSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const input = textAreaRef.current;
    if (input) {
      if (setValue) {
        setValue(input.value + '$' + event.target.value);
      }
    }
  };

  return (
    <div className={clsx('flex flex-col', className)}>
      <div className="relative flex justify-between">
        {label && (
          <label
            htmlFor={registerName}
            className={clsx('text-sm mb-1', {
              'absolute -top-5': absoluteLabel,
            })}>
            {label}
          </label>
        )}
        {whisper && (
          <select className="text-sm bg-transparent focus:outline-none" value="" onChange={handleWhisperSelect}>
            <option disabled value="">
              {whisperLabel}
            </option>
            {whisper.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </div>
      <div
        className={clsx(
          'flex text-base items-center pl-2 py-1 rounded-md focus-within::outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent',
          {
            'border-red-500': error,
            'bg-gray-200': isDisabled,
            [variantClass]: !error && !isDisabled,
          }
        )}>
        <textarea
          id={registerName}
          className={clsx('w-full bg-transparent focus:outline-none p-2 h-full', inputClassName)}
          disabled={isDisabled}
          placeholder={placeholder}
          ref={(e) => {
            ref(e);
            textAreaRef.current = e;
          }}
          {...rest}
          {...others}
        />
      </div>
      {error && <span className="text-xs text-red-500">{error.message}</span>}
    </div>
  );
};
